import * as Yup from 'yup'
import * as R from 'ramda'
import he from 'he'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import { removeHTMLTags } from 'libs/text'
import {
  MAX_PLACE_NAME_FOR_SMS,
  TYPE_SEND_SCHEDULE,
} from 'app/constants/compose'
import { MEDIUM_DATE_TIME_FORMAT } from 'libs/dateUtils'
import { checkErrorRatio } from './socials/useSocialMediaSetting'
import { isEmptyOrNil } from 'libs/empty'
import {
  TWITTER_VALUE,
  LINKEDIN_VALUE,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  CHANNEL_SMS,
  LINE_VALUE,
} from 'app/constants/channel'
import { TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'

const EVENT_ERROR_LABEL = 'composeDialog.event.field.required'
const TITLE_ERROR_LABEL = 'composeDialog.title.field.required'

const SCHEDULE_MIN_DATE_ERROR_ID =
  'sendMessage.dialog.validation.schedule.after.date'
const SCHEDULE_MAX_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.before.date'
const REMINDER_MIN_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.after.date'
const REMINDER_MAX_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.before.date'
const START_MIN_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.after.date'
const START_MAX_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.before.date'
const END_MIN_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.after.date'
const END_MAX_DATE_ERROR_ID =
  'createBeaconNotification.dialog.validation.before.date'

const checkChannelHasSocial = (channels) => {
  const pageIds = channels.pageIds
  const channelIds = channels.channelIds
  return (
    pageIds.length > 0 ||
    R.compose(
      R.gt(R.__, 0),
      R.length,
      R.filter(
        R.includes(R.__, [
          FACEBOOK_VALUE,
          TWITTER_VALUE,
          LINKEDIN_VALUE,
          INSTAGRAM_VALUE,
          LINE_VALUE,
        ]),
      ),
    )(channelIds)
  )
}

export const validationDate = (
  date,
  minDate,
  maxDate,
  timezone,
  isValidating,
  minErrorlabelId,
  maxErrorlabelId,
  formatMessage,
) => {
  if (date === undefined || date === null) {
    return undefined
  }
  const minDateUtc = moment(minDate).set({ second: 0, millisecond: 0 })
  const maxDateUtc = moment(maxDate).set({ second: 0, millisecond: 0 })

  const dateMoment = moment(date).set({ second: 0, millisecond: 0 })

  const getTextErrorDate = (id, dateDisplay) => {
    return formatMessage(
      { id },
      {
        newDate: momentTimezone(dateDisplay)
          .tz(timezone)
          .format(MEDIUM_DATE_TIME_FORMAT),
      },
    )
  }

  if (minDateUtc.isAfter(dateMoment) && isValidating) {
    const dateDisplay = minDateUtc.clone().subtract({ minutes: 1 })
    const text = getTextErrorDate(minErrorlabelId, dateDisplay)
    return text
  }

  if (maxDateUtc.isBefore(dateMoment) && isValidating) {
    const dateDisplay = maxDateUtc.clone()
    const text = getTextErrorDate(maxErrorlabelId, dateDisplay)
    return text
  }
  return undefined
}

export const validationGeneral = (
  initialValue,
  isEditMode,
  formatMessage,
  place,
) => {
  return Yup.object().shape({
    title: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const isPoll = parseInt(context.parent.notiType) === TYPE_POLL

          if (isPoll) {
            return true
          }
          let textId = isEvent ? EVENT_ERROR_LABEL : TITLE_ERROR_LABEL
          if (R.isNil(value)) {
            return context.createError({
              message: formatMessage({ id: textId }),
            })
          }
          const decodeValue = he.decode(removeHTMLTags(value))

          if (R.isEmpty(decodeValue)) {
            return context.createError({
              message: formatMessage({ id: textId }),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          let textId = 'createNotification.dialog.validate.maxLength.message'
          if (R.isNil(value)) {
            return true
          }
          const decodeValue = he.decode(removeHTMLTags(value))
          if (decodeValue.length > 200) {
            return context.createError({
              message: formatMessage(
                { id: textId },
                { maxLength: decodeValue.length - 200 },
              ),
            })
          }
          return true
        },
      }),
    notiType: Yup.string().required(),
    sendOptions: Yup.string().required(),
    body: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.body.field.required',
        }),
        test: function (value, context) {
          const isPoll = parseInt(context.parent.notiType) === TYPE_POLL
          if (isPoll) {
            return true
          }

          // Check if there are images in mediaSocials (synced from body editor)
          const mediaSocials = context.parent.mediaSocials || []
          const hasMediaSocialImages = mediaSocials.some(
            (media) => media.type === 'image' && media.bodyImageId,
          )

          if (R.isNil(value)) {
            // Body is undefined/null but may have images synced to mediaSocials
            return hasMediaSocialImages
          }
          const bodyDecode = he.decode(removeHTMLTags(value))

          const hasImages = /<img\s+[^>]*src\s*=\s*["'][^"']*["']/gi.test(value)
          const hasVideos = /<video\s+[^>]*src\s*=\s*["'][^"']*["']/gi.test(
            value,
          )
          const hasFileLinks =
            /<a\s+[^>]*href\s*=\s*["'][^"']*["'][^>]*download/gi.test(value)
          const hasIframes = /<iframe\s+[^>]*src\s*=\s*["'][^"']*["']/gi.test(
            value,
          )
          return (
            !R.isEmpty(bodyDecode) ||
            hasImages ||
            hasVideos ||
            hasFileLinks ||
            hasIframes ||
            hasMediaSocialImages
          )
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isPoll = parseInt(context.parent.notiType) === TYPE_POLL
          if (isPoll) {
            return true
          }
          if (R.isNil(value)) {
            // Body is undefined/null - skip max length check (required test handles this)
            return true
          }
          const bodyDecode = he.decode(removeHTMLTags(value))
          if (bodyDecode.length > 10000) {
            return context.createError({
              message: formatMessage(
                { id: 'createNotification.dialog.validate.maxLength.message' },
                { maxLength: bodyDecode.length - 10000 },
              ),
            })
          }
          return true
        },
      }),
    recipients: Yup.object().test({
      name: 'required',
      exclusive: false,
      params: {},
      message: formatMessage({
        id: 'composeDialog.GroupAndIndividual.field.required',
      }),
      test: function (value, context) {
        const channels = context.parent.channels
        if (channels === undefined) {
          return false
        }
        const hasSocialChannel = checkChannelHasSocial(channels)
        return hasSocialChannel
          ? true
          : value.subscribers.length > 0 || value.subGroups.length > 0
      },
    }),
    channels: Yup.object().test({
      name: 'required',
      exclusive: false,
      message: formatMessage({
        id: 'composeDialog.channels.field.required',
      }),
      test: function (value) {
        const pageIds = value.pageIds
        const channelIds = value.channelIds
        return !(pageIds.length === 0 && channelIds.length === 0)
      },
    }),
    mediaSocials: Yup.array()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'createNotification.dialog.title.selector.image.requires',
        }),
        test: function (value, context) {
          return !(
            R.includes(INSTAGRAM_VALUE, context.parent.channels.channelIds) &&
            value.length === 0
          )
        },
      })
      .test({
        name: 'ratio',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'createNotification.dialog.instagram.error.ratio',
        }),
        test: async function (value, context) {
          if (R.includes(INSTAGRAM_VALUE, context.parent.channels.channelIds)) {
            const mediaSelected = R.find(R.prop('isSelected'), value)
            if (mediaSelected === undefined) {
              return true
            } else {
              const errorRatio = await checkErrorRatio(mediaSelected)
              return !errorRatio
            }
          }
          return true
        },
      }),
    eventStartDateTime: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.eventDate.field.required',
        }),
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          if (isEvent) {
            return !R.isNil(value)
          }
          return true
        },
      })
      .test({
        name: 'min',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const minCurrentDate = moment().set({ second: 0, millisecond: 0 })
          const dateValue = momentTimezone.tz(
            value,
            context.parent.eventTimezone,
          )
          const minEventStart = moment().set({ second: 0, millisecond: 0 })
          let minDate = momentTimezone(minEventStart).tz(
            context.parent.eventTimezone,
          )
          if (minEventStart.isBefore(minCurrentDate)) {
            minDate = momentTimezone(minCurrentDate).tz(
              context.parent.eventTimezone,
            )
          }

          if (isEditMode) {
            const originalValue = momentTimezone.tz(
              value,
              context.parent.eventTimezone,
            )

            if (minDate.isAfter(originalValue)) {
              return true
            }
          }

          minDate = minDate.subtract({ hours: 24 })
          if (minDate.isAfter(dateValue) && isEvent) {
            const dateDisplay = minDate
              .subtract({ minutes: 1 })
              .format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: START_MIN_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const dateValue = momentTimezone.tz(
            value,
            context.parent.eventTimezone,
          )

          const maxStartEvent = moment()
            .add(10, 'year')
            .set({ second: 0, millisecond: 0 })
          const maxDate = momentTimezone(maxStartEvent).tz(
            context.parent.eventTimezone,
          )

          if (isEditMode) {
            return true
          }

          if (maxDate.isBefore(dateValue) && isEvent) {
            const dateDisplay = maxDate.format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: START_MAX_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      }),
    eventEndDateTime: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.eventDate.field.required',
        }),
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          if (isEvent) {
            return !R.isNil(value)
          }
          return true
        },
      })
      .test({
        name: 'min',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const dateValue = momentTimezone.tz(
            value,
            context.parent.eventTimezone,
          )
          let minDate = momentTimezone.tz(
            context.parent.eventStartDateTime,
            context.parent.eventTimezone,
          )
          if (minDate.isAfter(dateValue) && isEvent) {
            const dateDisplay = minDate
              .subtract({ minutes: 1 })
              .format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: END_MIN_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const dateValue = momentTimezone.tz(
            value,
            context.parent.eventTimezone,
          )
          const maxEvent = moment()
            .add(10, 'year')
            .set({ second: 0, millisecond: 0 })
          const maxDate = momentTimezone(maxEvent).tz(
            context.parent.eventTimezone,
          )
          if (maxDate.isBefore(dateValue) && isEvent) {
            const dateDisplay = maxDate.format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: END_MAX_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      }),
    eventTimezone: Yup.string().optional(),
    location: Yup.object().test({
      name: 'required-and-length',
      exclusive: false,
      params: {},
      message: formatMessage({ id: 'composeDialog.location.field.required' }),
      test: function (value, context) {
        const { notiType } = context.parent

        if (parseInt(notiType) === TYPE_EVENT && R.isNil(value?.suggestion)) {
          return this.createError({
            message: formatMessage({
              id: 'composeDialog.location.field.required',
            }),
          })
        }

        if (value?.suggestion) {
          if (value.suggestion.length > 190) {
            return this.createError({
              message: formatMessage(
                { id: 'createNotification.dialog.validate.maxLength.message' },
                { maxLength: value.suggestion.length - 190 },
              ),
            })
          }
        }

        return true
      },
    }),
    schedule: Yup.string()
      .test({
        name: 'intoday',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.validation.schedule.intoday',
        }),
        test: function (value, context) {
          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE
          if (context.options.parent.eventStartDateTime && isSchedule) {
            const eventStartDateTime = context.options.parent.eventStartDateTime
            if (moment().isSameOrAfter(eventStartDateTime, 'day')) {
              return false
            }
          }

          return true
        },
      })
      .test({
        name: 'min',
        exclusive: false,
        params: {},
        test: function (value, context) {
          if (R.isNil(value)) {
            return true
          }
          let message = undefined
          const scheduleDate = moment(value)
          const minDateCurrent = moment().add({ hour: 1 })

          if (scheduleDate.isSameOrBefore(minDateCurrent)) {
            const date = momentTimezone.tz(
              minDateCurrent.format(),
              context.parent.eventTimezone,
            )
            message = formatMessage(
              { id: SCHEDULE_MIN_DATE_ERROR_ID },
              {
                newDate: date.format(MEDIUM_DATE_TIME_FORMAT),
              },
            )
          }

          return !R.isNil(message)
            ? context.createError({
                message: message,
              })
            : true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        test: function (value, context) {
          if (R.isNil(value)) {
            return true
          }
          let message = undefined
          const scheduleDate = moment(value)
          const maxDate = moment().add({ years: 10 })
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          const isPoll = parseInt(context.parent.notiType) === TYPE_POLL
          if (isEvent) {
            const eventStartDate = R.isNil(context.parent.eventStartDateTime)
              ? maxDate
              : moment(context.parent.eventStartDateTime)
            if (scheduleDate.isSameOrAfter(eventStartDate)) {
              const date = momentTimezone.tz(
                context.parent.eventStartDateTime,
                context.parent.eventTimezone,
              )
              message = formatMessage(
                { id: SCHEDULE_MAX_DATE_ERROR_ID },
                {
                  newDate: date.format(MEDIUM_DATE_TIME_FORMAT),
                },
              )
            }
          }

          if (isPoll) {
            const pollEndDate = R.isNil(context.parent.pollEndDateTime)
              ? maxDate
              : moment(context.parent.pollEndDateTime)

            if (scheduleDate.isSameOrAfter(pollEndDate)) {
              const date = momentTimezone.tz(
                context.parent.pollEndDateTime,
                context.parent.pollTimezone,
              )
              message = formatMessage(
                { id: SCHEDULE_MAX_DATE_ERROR_ID },
                {
                  newDate: date.format(MEDIUM_DATE_TIME_FORMAT),
                },
              )
            }
          }

          return !R.isNil(message)
            ? context.createError({
                message: message,
              })
            : true
        },
      }),
    scheduleTimezone: Yup.string().optional(),
    reminderChecked: Yup.boolean().required(),
    reminder: Yup.string()
      .test({
        name: 'min',
        exclusive: false,
        test: function (value, context) {
          if (R.isNil(value)) {
            return true
          }
          let message = undefined

          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE
          const reminderDate = moment(value)
          const minDateCurrent = isSchedule
            ? moment(context.parent.schedule)
                .add({ hour: 1 })
                .subtract({ minute: 1 })
            : moment().add({ hour: 1 }).subtract({ minute: 1 })

          if (reminderDate.isSameOrBefore(minDateCurrent)) {
            const date = momentTimezone.tz(
              minDateCurrent.format(),
              context.parent.reminderTimezone,
            )
            message = formatMessage(
              { id: REMINDER_MIN_DATE_ERROR_ID },
              {
                newDate: date.format(MEDIUM_DATE_TIME_FORMAT),
              },
            )
          }
          return !R.isNil(message)
            ? context.createError({
                message: message,
              })
            : true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          const isReminder = context.parent.reminderChecked
          const dateValue = momentTimezone.tz(
            value,
            context.parent.reminderTimezone,
          )
          const maxReminder = moment()
            .add(10, 'year')
            .set({ second: 0, millisecond: 0 })
          const maxDate = momentTimezone(maxReminder).tz(
            context.parent.reminderTimezone,
          )
          if (maxDate.isBefore(dateValue) && isReminder) {
            const dateDisplay = maxDate.format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: REMINDER_MAX_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      }),
    reminderTimezone: Yup.string().optional(),
    shortName: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        message: formatMessage({
          id: 'composeDialog.shortName.field.required',
        }),
        test: function (value, context) {
          if (!R.isNil(place.info.shortName)) {
            return true
          }
          return !(
            R.includes(CHANNEL_SMS, context.parent.channels.channelIds) &&
            R.isNil(value)
          )
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        message: 'Short name max',
        test: function (value) {
          if (!R.isNil(value)) {
            return value.length <= MAX_PLACE_NAME_FOR_SMS
          }
          return true
        },
      }),
    pollEndDateTime: Yup.string().test({
      name: 'min',
      exclusive: false,
      test: function (value, context) {
        const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
        if (R.isNil(value) || isNotPoll) {
          return true
        }

        const dateValue = momentTimezone.tz(value, context.parent.pollTimezone)
        const minDate = moment()
          .set({ second: 0, millisecond: 0 })
          .add({ hours: 1 })

        if (minDate.isAfter(dateValue)) {
          const dateDisplay = minDate.format(MEDIUM_DATE_TIME_FORMAT)
          return context.createError({
            message: formatMessage(
              { id: END_MIN_DATE_ERROR_ID },
              {
                newDate: dateDisplay,
              },
            ),
          })
        }
        return true
      },
    }),
    question: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.question.field.required',
        }),
        test: function (value, context) {
          const isPoll = parseInt(context.parent.notiType) === TYPE_POLL
          return isPoll ? !R.isNil(value) : true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        test: function (value, context) {
          let textId = 'createNotification.dialog.validate.maxLength.message'
          const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
          if (isNotPoll) {
            return true
          }
          if (R.isNil(value)) {
            return true
          }
          if (value.length > 200) {
            return context.createError({
              message: formatMessage(
                { id: textId },
                { maxLength: value.length - 200 },
              ),
            })
          }
          return true
        },
      }),
    options: Yup.array()
      .test('title-length', '', function (value, context) {
        const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
        if (isNotPoll) {
          return true
        }
        return value
          ? value.every((option) => R.length(option.title || '') <= 200)
          : true
      })
      .test(
        'required-min-two-options',
        formatMessage({
          id: 'createNotification.validation.min.poll.option',
        }),
        function (value, context) {
          const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
          if (isNotPoll) {
            return true
          }
          const hasValue2Items = R.filter((option) => {
            return !isEmptyOrNil(R.trim(option.title || ''))
          }, value)
          return hasValue2Items.length > 1
        },
      )
      .test(
        'required-max-twenty-options',
        formatMessage({
          id: 'createNotification.validation.max.poll.option',
        }),
        function (value, context) {
          const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
          if (isNotPoll) {
            return true
          }
          const hasValue2Items = R.filter((option) => {
            return !isEmptyOrNil(R.trim(option.title || ''))
          }, value)
          return hasValue2Items.length <= 20
        },
      )
      .test(
        'no-duplicate-titles',
        formatMessage({
          id: 'createNotification.validation.duplicate.poll.option',
        }),
        function (value, context) {
          const isNotPoll = parseInt(context.parent.notiType) !== TYPE_POLL
          if (isNotPoll) {
            return true
          }

          const titles = R.compose(
            R.map(R.prop('title')),
            R.filter((item) => !isEmptyOrNil(item.title)),
          )(value || [])

          const uniqueTitles = R.uniq(titles)
          return uniqueTitles.length === titles.length
        },
      ),
    additionalContext: Yup.string(),
  })
}

export const validationBeacon = (
  initialValue,
  isEditMode,
  formatMessage,
  place,
) => {
  return Yup.object().shape({
    title: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        test: function (value, context) {
          const isEvent = parseInt(context.parent.notiType) === TYPE_EVENT
          let textId = isEvent ? EVENT_ERROR_LABEL : TITLE_ERROR_LABEL
          if (R.isNil(value)) {
            return context.createError({
              message: formatMessage({ id: textId }),
            })
          }
          const decodeValue = he.decode(removeHTMLTags(value))

          if (R.isEmpty(decodeValue)) {
            return context.createError({
              message: formatMessage({ id: textId }),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          let textId = 'createNotification.dialog.validate.maxLength.message'
          if (R.isNil(value)) {
            return true
          }
          const decodeValue = he.decode(removeHTMLTags(value))
          if (decodeValue.length > 200) {
            return context.createError({
              message: formatMessage(
                { id: textId },
                { maxLength: decodeValue.length - 200 },
              ),
            })
          }
          return true
        },
      }),
    notiType: Yup.string().required(),
    sendOptions: Yup.string().required(),
    body: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'composeDialog.body.field.required',
        }),
        test: function (value, context) {
          // Check if there are images in mediaSocials (synced from body editor)
          const mediaSocials = context.parent.mediaSocials || []
          const hasMediaSocialImages = mediaSocials.some(
            (media) => media.type === 'image' && media.bodyImageId,
          )

          if (R.isNil(value)) {
            // Body is undefined/null but may have images synced to mediaSocials
            return hasMediaSocialImages
          }
          const bodyDecode = he.decode(removeHTMLTags(value))
          const hasImages = /<img\s+[^>]*src\s*=\s*["'][^"']*["']/gi.test(value)
          const hasVideos = /<video\s+[^>]*src\s*=\s*["'][^"']*["']/gi.test(
            value,
          )
          const hasFileLinks =
            /<a\s+[^>]*href\s*=\s*["'][^"']*["'][^>]*download/gi.test(value)

          return (
            !R.isEmpty(bodyDecode) ||
            hasImages ||
            hasVideos ||
            hasFileLinks ||
            hasMediaSocialImages
          )
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        params: {},
        test: function (value, context) {
          if (R.isNil(value)) {
            // Body is undefined/null - skip max length check (required test handles this)
            return true
          }
          const bodyDecode = he.decode(removeHTMLTags(value))
          if (bodyDecode.length > 10000) {
            return context.createError({
              message: formatMessage(
                { id: 'createNotification.dialog.validate.maxLength.message' },
                { maxLength: bodyDecode.length - 10000 },
              ),
            })
          }
          return true
        },
      }),
    recipients: Yup.object().test({
      name: 'required',
      exclusive: false,
      params: {},
      message: formatMessage({
        id: 'composeDialog.GroupAndIndividual.field.required',
      }),
      test: function (value, context) {
        const channels = context.parent.channels
        if (channels === undefined) {
          return false
        }
        const hasSocialChannel = checkChannelHasSocial(channels)
        return hasSocialChannel
          ? true
          : value.subscribers.length > 0 || value.subGroups.length > 0
      },
    }),
    channels: Yup.object().test({
      name: 'required',
      exclusive: false,
      params: {},
      message: formatMessage({
        id: 'composeDialog.channels.field.required',
      }),
      test: function (value, context) {
        const pageIds = value.pageIds
        const channelIds = value.channelIds
        return !(pageIds.length === 0 && channelIds.length === 0)
      },
    }),
    mediaSocials: Yup.array()
      .test({
        name: 'required',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'createNotification.dialog.title.selector.image.requires',
        }),
        test: function (value, context) {
          return !(
            R.includes(INSTAGRAM_VALUE, context.parent.channels.channelIds) &&
            value.length === 0
          )
        },
      })
      .test({
        name: 'ratio',
        exclusive: false,
        params: {},
        message: formatMessage({
          id: 'createNotification.dialog.instagram.error.ratio',
        }),
        test: async function (value, context) {
          if (R.includes(INSTAGRAM_VALUE, context.parent.channels.channelIds)) {
            const mediaSelected = R.find(R.prop('isSelected'), value)
            if (mediaSelected === undefined) {
              return true
            } else {
              const errorRatio = await checkErrorRatio(mediaSelected)
              return !errorRatio
            }
          }
          return true
        },
      }),
    shortName: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        message: formatMessage({
          id: 'composeDialog.shortName.field.required',
        }),
        test: function (value, context) {
          if (!R.isNil(place.info.shortName)) {
            return true
          }
          return !(
            R.includes(CHANNEL_SMS, context.parent.channels.channelIds) &&
            R.isNil(value)
          )
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        message: 'Short name max',
        test: function (value) {
          if (!R.isNil(value)) {
            return value.length <= MAX_PLACE_NAME_FOR_SMS
          }
          return true
        },
      }),
    scheduleStartDate: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        message: formatMessage({
          id: 'composeDialog.ScheduleDate.field.required',
        }),
        test: function (value, context) {
          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE

          if (isSchedule) {
            return !R.isNil(value)
          }

          return true
        },
      })
      .test({
        name: 'min',
        exclusive: false,
        test: function (value, context) {
          const minCurrentDate = moment().set({ second: 0, millisecond: 0 })
          const dateValue = momentTimezone.tz(
            value,
            context.parent.scheduleBeaconTimezone,
          )
          const minStartDate = moment().set({ second: 0, millisecond: 0 })
          let minDate = momentTimezone(minStartDate).tz(
            context.parent.scheduleBeaconTimezone,
          )
          if (minStartDate.isBefore(minCurrentDate)) {
            minDate = momentTimezone(minCurrentDate).tz(
              context.parent.scheduleBeaconTimezone,
            )
          }

          if (isEditMode) {
            const originalValue = momentTimezone.tz(
              initialValue.scheduleStartDate,
              context.parent.scheduleBeaconTimezone,
            )
            if (minDate.isAfter(originalValue)) {
              return true
            }
          }

          minDate = minDate.subtract({ hours: 24 })
          if (minDate.isAfter(dateValue)) {
            const dateDisplay = minDate
              .subtract({ minutes: 1 })
              .format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: START_MIN_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        test: function (value, context) {
          const dateValue = momentTimezone.tz(
            value,
            context.parent.scheduleBeaconTimezone,
          )
          const maxStartEvent = moment()
            .add(10, 'year')
            .set({ second: 0, millisecond: 0 })
          const maxDate = momentTimezone(maxStartEvent).tz(
            context.parent.scheduleBeaconTimezone,
          )
          if (maxDate.isBefore(dateValue)) {
            const dateDisplay = maxDate.format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: START_MAX_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      }),
    scheduleEndDate: Yup.string()
      .test({
        name: 'required',
        exclusive: false,
        message: formatMessage({
          id: 'composeDialog.ScheduleDate.field.required',
        }),
        test: function (value, context) {
          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE

          if (
            context.parent.addEndDateSchedule &&
            isSchedule &&
            context.parent.scheduleStartDate
          ) {
            return !R.isNil(value)
          }

          return true
        },
      })
      .test({
        name: 'min',
        exclusive: false,
        test: function (value, context) {
          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE

          const dateValue = momentTimezone.tz(
            value,
            context.parent.scheduleBeaconTimezone,
          )
          let minDate = momentTimezone.tz(
            context.parent.scheduleStartDate,
            context.parent.scheduleBeaconTimezone,
          )

          if (
            minDate.isAfter(dateValue) &&
            isSchedule &&
            context.parent.addEndDateSchedule
          ) {
            const dateDisplay = minDate
              .subtract({ minutes: 1 })
              .format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: END_MIN_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      })
      .test({
        name: 'max',
        exclusive: false,
        test: function (value, context) {
          const isSchedule =
            context.options.parent.sendOptions === TYPE_SEND_SCHEDULE
          if (!context.parent.addEndDateSchedule) {
            return true
          }
          const dateValue = momentTimezone.tz(
            value,
            context.parent.scheduleBeaconTimezone,
          )
          const maxEvent = moment()
            .add(10, 'year')
            .set({ second: 0, millisecond: 0 })
          const maxDate = momentTimezone(maxEvent).tz(
            context.parent.scheduleBeaconTimezone,
          )
          if (
            maxDate.isBefore(dateValue) &&
            isSchedule &&
            context.parent.addEndDateSchedule
          ) {
            const dateDisplay = maxDate.format(MEDIUM_DATE_TIME_FORMAT)
            return context.createError({
              message: formatMessage(
                { id: END_MAX_DATE_ERROR_ID },
                {
                  newDate: dateDisplay,
                },
              ),
            })
          }
          return true
        },
      }),
    scheduleBeaconTimezone: Yup.string().optional(),
    beacons: Yup.array()
      .min(1, 'You must select at least one beacon')
      .required('Beacon selection is required'),
    frequencyType: Yup.string(),
    duration: Yup.number().test({
      name: 'required',
      exclusive: false,
      message: formatMessage({
        id: 'composeDialog.duration.field.required',
      }),
      test: function (value, context) {
        const isOnce = context.parent.frequencyType === 'once'
        if (!isOnce) {
          return !R.isNil(value)
        }

        return true
      },
    }),
  })
}
